/**
 * SectionHeading — numbered step circle + title + description +
 * optional trailing slot for a section button (e.g. "Suggest
 * customer", "Lookup HSN").
 *
 * Renders as a self-contained head band — paper background, hairline
 * bottom border, edge-to-edge padding — that sits BETWEEN the Card
 * shell and the CardBody. Mirrors the prototype's S12 `.section-head`
 * which is structurally a header strip with a divider, not a row
 * inside the body padding.
 *
 *   <Card>
 *     <SectionHeading step={1} title="…" description="…" trailing={…} />
 *     <CardBody>
 *       … section body …
 *     </CardBody>
 *   </Card>
 *
 * The step number is inline inside the `h3` (not a separate column)
 * because the prototype's CSS puts `.step` inside the `<h3>` so the
 * numeric chip flows with the title — that's what gives the strip
 * its compact, single-baseline feel.
 *
 * @see prototype/sales/invoice-new.html — `.section-head` (CSS at L376)
 *      and the per-section markup at L1519, L1601, L1709, L1748,
 *      L1784.
 */
import type { ReactNode } from "react";

export interface SectionHeadingProps {
  /** 1, 2, 3, 4, 5 — small monospace circle inside the title. */
  step: number;
  title: string;
  description?: string;
  /** Trailing element (usually a "Suggest …" / "Lookup …" button). */
  trailing?: ReactNode;
}

export function SectionHeading({ step, title, description, trailing }: SectionHeadingProps) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line bg-paper px-[18px] pt-3.5 pb-2.5">
      <div className="min-w-0">
        <h3 className="m-0 inline-flex items-center gap-2 text-sm font-semibold text-ink leading-tight">
          <span
            aria-hidden
            className="inline-grid h-5 w-5 shrink-0 place-items-center rounded-full bg-bg text-muted font-mono text-xs font-bold"
          >
            {step}
          </span>
          {title}
        </h3>
        {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
      </div>
      {trailing ? <div className="shrink-0 flex items-center gap-1.5">{trailing}</div> : null}
    </div>
  );
}
